/**
 * URL helper functions for generating readable URLs throughout the application
 */

import { createJobSlug } from './slugify';
import { supabase } from './supabase';

/**
 * Generate a readable job details URL
 * @param {Object} job - Job object with id and title
 * @returns {string} - Readable job details URL
 */
export const getJobDetailsUrl = (job) => {
  if (!job || !job.id) return '/job-search';
  
  if (job.title) {
    const slug = createJobSlug(job.title, job.id);
    return `/job-details/${slug}`;
  }
  
  // Fallback to legacy format if no title
  return `/job/${job.id}`;
};

/**
 * Generate a job application URL with apply parameter
 * @param {Object} job - Job object with id and title
 * @returns {string} - Job details URL with apply=true parameter
 */
export const getJobApplicationUrl = (job) => {
  const baseUrl = getJobDetailsUrl(job);
  return `${baseUrl}?apply=true`;
};

/**
 * Generate readable URLs for other entities (can be extended)
 */

/**
 * Get company profile URL (placeholder for future implementation)
 * @param {Object} company - Company object
 * @returns {string} - Company profile URL
 */
export const getCompanyProfileUrl = (company) => {
  if (!company || !company.id) return '#';
  
  // For now, return a simple URL - can be enhanced with slugs later
  return `/company/${company.id}`;
};

/**
 * Get user profile URL (placeholder for future implementation)
 * @param {Object} user - User object
 * @returns {string} - User profile URL
 */
export const getUserProfileUrl = (user) => {
  if (!user || !user.id) return '#';
  
  // For now, return a simple URL - can be enhanced with slugs later
  return `/profile/${user.id}`;
};

/**
 * VA Slug Functions for Time Tracker URLs
 */

/**
 * Creates a URL-safe slug from a full name
 * @param {string} fullName - The full name to convert
 * @returns {string} URL-safe slug
 */
export const createVASlug = (fullName) => {
  if (!fullName) return '';
  
  return fullName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Parses a VA slug back to a readable name format
 * @param {string} slug - The URL slug to parse
 * @returns {string} Formatted name
 */
export const parseVASlug = (slug) => {
  if (!slug) return '';
  
  return decodeURIComponent(slug)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
};

/**
 * Validates if a slug has the correct format
 * @param {string} slug - The slug to validate
 * @returns {boolean} Whether the slug is valid
 */
export const isValidVASlug = (slug) => {
  return /^[a-z0-9-]+$/.test(slug) && !slug.startsWith('-') && !slug.endsWith('-');
};

/**
 * Creates a unique VA slug, handling duplicates by appending user ID suffix
 * @param {string} fullName - The full name to convert
 * @param {string} userId - Optional user ID for uniqueness
 * @returns {Promise<string>} Unique URL-safe slug
 */
export const createUniqueVASlug = async (fullName, userId = null) => {
  const baseSlug = createVASlug(fullName);
  
  try {
    // Check if slug already exists for different user
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .eq('role', 'va')
      .ilike('full_name', parseVASlug(baseSlug));

    if (error || !data) return baseSlug;

    // If multiple VAs have similar names, append ID suffix
    const duplicates = data.filter(va => va.id !== userId);
    if (duplicates.length > 0) {
      return `${baseSlug}-${userId?.slice(-8) || 'user'}`;
    }

    return baseSlug;
  } catch (error) {
    console.error('Error creating unique VA slug:', error);
    return baseSlug;
  }
};

/**
 * Gets a VA profile by their name slug
 * @param {string} nameSlug - The URL slug to search for
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getVABySlug = async (nameSlug) => {
  try {
    // Validate slug format
    if (!isValidVASlug(nameSlug)) {
      throw new Error('Invalid VA name format');
    }

    // Convert slug back to full name
    const fullName = parseVASlug(nameSlug);
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('role', 'va')
      .ilike('full_name', fullName) // Use ilike for case-insensitive search
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching VA by slug:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate time tracker URL with VA name slug
 * @param {Object} va - VA object with full_name
 * @returns {string} - Time tracker URL with name slug
 */
export const getTimeTrackerUrl = (va) => {
  if (!va || !va.full_name) return '/time-tracker';
  
  const vaSlug = createVASlug(va.full_name);
  return `/time-tracker/${vaSlug}`;
};
